import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { postPaypalFree, postOrder } from 'src/services/checkout';
import Paypal from 'src/services/paypal';
import PromotionChart from 'src/components/PromotionChart';
import useCustomHistory from 'src/hooks/useCustomHistory';
import { usePromotionCode } from '../PromotionCode';
import { usePaymentAgree } from '../Agree';
import PaymentEmail from '../Email';
import PaymentReceiptSubscription from '../ReceiptSubscription';
import PaymentReceipt from '../Receipt';
import LoadingBlock from 'src/components/LoadingBlock';
import { useModal } from 'src/ModalContext';
import Badge from 'src/components/Badge';
import { isKorean } from 'src/services/util';
import StickyButton from 'src/components/StickyButton';

const paypal = new Paypal();

export default function PaymentPaypal({ product, isUsed }: { product: CheckoutProductData; isUsed: boolean }): JSX.Element {
  const { t } = useTranslation();
  const history = useCustomHistory();
  const [isEnablePay, setIsEnablePay] = useState(false);
  const [isLoadingShow, setIsLoadingShow] = useState(false);
  const { errorModal } = useModal();

  const { promotion, PromotionCode } = usePromotionCode({
    product,
  });

  const { checked, PaymentAgree } = usePaymentAgree();

  useEffect(() => {
    if (product.id) {
      (async function load() {
        try {
          await paypal.init(product, {
            onLoadingStart() {
              setIsLoadingShow(true);
            },
            onLoadingEnd() {
              setIsLoadingShow(false);
            },
            onComplete(search) {
              setIsLoadingShow(false);
              history.push('/payment/complete', search);
            },
            onError(search) {
              setIsLoadingShow(false);
              history.push('/payment/fail', search);
            },
          });
        } catch (e) {
          errorModal(e.message);
        }
      })();
    }
    return () => {
      paypal.destroy();
    };
  }, [product]);

  const onBuyFreeItem = useCallback(async () => {
    try {
      const order = await postOrder({
        productId: product.id,
        ...(promotion
          ? {
              promotionId: promotion.id,
            }
          : {}),
      });

      await postPaypalFree({
        orderId: order.order.id,
        promotionCode: promotion.code,
      });

      history.push('/payment/complete', {
        orderId: order.order.id,
        productId: product.id,
        imp_success: 'true',
      });
    } catch (e) {
      history.push('/payment/fail', {
        productId: product.id,
        error_msg: e.message,
      });
    }
  }, [product, promotion]);

  useEffect(() => {
    paypal.setPromotionCode(promotion);
  }, [promotion.id]);

  useEffect(() => {
    if (product.subscription) {
      setIsEnablePay(checked);
    } else {
      setIsEnablePay(true);
    }
  }, [product, checked]);

  return (
    <>
      <div className="container mt-6">
        {product.subscription && (
          <div className="mb-3">
            <Badge color="light-grey-blue" title={t('payment/complete/badge_billing')}></Badge>
          </div>
        )}
        <h1 className="N_20_R text-black">{isKorean() ? product.koName : product.enName}</h1>
      </div>
      <div className="mt-6 divide-line"></div>
      <div className="container">
        {!isUsed && product.subscription && <PromotionChart product={product} />}
        {!product.subscription && PromotionCode}
        <PaymentEmail />
      </div>
      {product.subscription && PaymentAgree}
      {product.subscription ? (
        <PaymentReceiptSubscription product={product} isUsed={isUsed} />
      ) : (
        <PaymentReceipt product={product} promotion={promotion} />
      )}
      <div className="mt-10 pt-6 pb-10 bg-pale-grey">
        <ul className="container list-disc N_12_R text-brown-grey">
          <li>{t('payment/disclaimer_1')}</li>
          <li>{t('payment/disclaimer_2')}</li>
        </ul>
      </div>
      <div className="sticky bottom-0 w-full">
        {!product.subscription && promotion.saleAmount && product.amount - promotion.saleAmount === 0 && (
          <StickyButton title={t('payment/purchase_button_title')} onClick={onBuyFreeItem} disabled={!isEnablePay}></StickyButton>
        )}
        <div
          id="paypal-button"
          className={
            `flex justify-center` +
            (isEnablePay ? ' active' : ' pointer-events-none') +
            (promotion.saleAmount && product.amount - promotion.saleAmount === 0 ? ' hidden' : '')
          }
        ></div>
      </div>
      <LoadingBlock title={t('payment/payment_loading')} isShow={isLoadingShow} />
    </>
  );
}
